import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiUsers, 
  FiTrendingUp, 
  FiCalendar, 
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo
} = FiIcons;

const CompetitiveIntelligence = ({ productId }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Mock KPI data
  const kpis = [
    { label: 'Total ANDA Holders', value: '12', icon: FiUsers },
    { label: 'Total Active DMF Holders', value: '8', icon: FiCheckCircle },
    { label: 'Vertically Integrated Companies', value: '5', icon: FiTrendingUp }
  ];

  // Mock competitive data
  const competitiveData = [
    {
      id: 1,
      genericCompany: 'Biocon Pharmaceuticals',
      andaHolder: 'Biocon Pharmaceuticals',
      apiSupplier: 'Biocon Biologics (Captive)',
      expectedLaunchDate: '2024-02-15',
      exportIntel: 'Active exports to EU, Canada',
      comments: 'Strong vertical integration, no recent 483s',
      firstFiler: true,
      verticallyIntegrated: true,
      apiPrice: 2850
    },
    {
      id: 2,
      genericCompany: 'Dr. Reddy\'s Laboratories',
      andaHolder: 'Dr. Reddy\'s Laboratories',
      apiSupplier: 'Aurobindo Pharma',
      expectedLaunchDate: '2024-03-10',
      exportIntel: 'Limited export activity',
      comments: 'Recent FDA inspection completed',
      firstFiler: false,
      verticallyIntegrated: false,
      apiPrice: 2920
    },
    {
      id: 3,
      genericCompany: 'Aurobindo Pharma',
      andaHolder: 'Aurobindo Pharma USA',
      apiSupplier: 'Aurobindo Pharma (Captive)',
      expectedLaunchDate: '2024-04-05',
      exportIntel: 'Major supplier to global markets',
      comments: 'Vertically integrated, strong API capabilities',
      firstFiler: false,
      verticallyIntegrated: true,
      apiPrice: 2780
    },
    {
      id: 4,
      genericCompany: 'Cipla Ltd',
      andaHolder: 'Cipla USA Inc',
      apiSupplier: 'Hetero Drugs',
      expectedLaunchDate: '2024-05-20',
      exportIntel: 'Focus on emerging markets',
      comments: 'Dossier under review, minor 483 observations',
      firstFiler: false,
      verticallyIntegrated: false,
      apiPrice: 3100
    },
    {
      id: 5,
      genericCompany: 'Sun Pharmaceutical',
      andaHolder: 'Sun Pharma Global FZE',
      apiSupplier: 'Sun Pharma (Captive)',
      expectedLaunchDate: '2024-06-15',
      exportIntel: 'Established global presence',
      comments: 'No compliance issues, strong track record',
      firstFiler: false,
      verticallyIntegrated: true,
      apiPrice: 2950
    }
  ];

  // Mock API supplier data for Sankey chart
  const apiSuppliers = [
    { name: 'Biocon Biologics', companies: ['Biocon Pharmaceuticals'], price: 2850 },
    { name: 'Aurobindo Pharma', companies: ['Dr. Reddy\'s Laboratories', 'Aurobindo Pharma'], price: 2850 },
    { name: 'Hetero Drugs', companies: ['Cipla Ltd'], price: 3100 },
    { name: 'Sun Pharma API', companies: ['Sun Pharmaceutical'], price: 2950 },
    { name: 'Mylan API', companies: ['Viatris Inc'], price: 3200 }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Data Source */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-blue-50 border border-blue-200 rounded-xl p-4"
      >
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiInfo} className="text-blue-600" />
          <div>
            <p className="font-medium text-blue-900">Data Source Information</p>
            <p className="text-sm text-blue-700">
              Per Orange Book and IPD Analytics, Exim Data & Secondary Research
            </p>
          </div>
        </div>
      </motion.div>

      {/* KPI Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {kpis.map((kpi, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={kpi.icon} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
              </div>
            </div>
            <p className="text-gray-600 font-medium">{kpi.label}</p>
          </div>
        ))}
      </motion.div>

      {/* ANDA Competition Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiUsers} className="text-blue-600" />
            ANDA Competition Analysis
          </h3>
          <p className="text-gray-600 mt-1">
            Generic manufacturers and API supplier relationships
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Generic Company</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">ANDA Holder</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">API Supplier</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Expected Launch</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {competitiveData.map((company, index) => (
                <React.Fragment key={company.id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium text-gray-900">{company.genericCompany}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {company.firstFiler && (
                              <span className="inline-flex px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                First Filer
                              </span>
                            )}
                            {company.verticallyIntegrated && (
                              <span className="inline-flex px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                Vertical Integration
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-gray-700">{company.andaHolder}</span>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <p className="text-gray-900">{company.apiSupplier}</p>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(company.apiPrice)}/kg
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiCalendar} className="text-gray-400 text-sm" />
                        <span className="text-gray-700">{formatDate(company.expectedLaunchDate)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                        <span className="text-sm text-green-700">Active</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => toggleRow(company.id)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                      >
                        {expandedRows.has(company.id) ? 'Hide Details' : 'Show Details'}
                      </button>
                    </td>
                  </tr>
                  
                  {/* Expanded Row */}
                  {expandedRows.has(company.id) && (
                    <tr className="bg-gray-50">
                      <td colSpan="6" className="py-4 px-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Export Intelligence</h4>
                            <p className="text-sm text-gray-700">{company.exportIntel}</p>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Comments</h4>
                            <p className="text-sm text-gray-700">{company.comments}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* API Supplier Analysis */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <SafeIcon icon={FiTrendingUp} className="text-blue-600" />
          API Supplier Network Analysis
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Supplier List */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">API Suppliers & Pricing</h4>
            <div className="space-y-3">
              {apiSuppliers.map((supplier, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-medium text-gray-900">{supplier.name}</h5>
                    <span className="text-sm font-medium text-blue-600">
                      {formatCurrency(supplier.price)}/kg
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Supplies to: {supplier.companies.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Market Insights */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Market Insights</h4>
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                  <h5 className="font-medium text-green-900">Competitive Advantage</h5>
                </div>
                <p className="text-sm text-green-800">
                  5 out of 12 ANDA holders are vertically integrated, providing cost advantages
                </p>
              </div>
              
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiAlertTriangle} className="text-yellow-600" />
                  <h5 className="font-medium text-yellow-900">Market Risk</h5>
                </div>
                <p className="text-sm text-yellow-800">
                  API price volatility ranges from $2,780 to $3,200 per kg across suppliers
                </p>
              </div>
              
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiInfo} className="text-blue-600" />
                  <h5 className="font-medium text-blue-900">Launch Timeline</h5>
                </div>
                <p className="text-sm text-blue-800">
                  Expected generic launches spread over 4 months (Feb-Jun 2024)
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CompetitiveIntelligence;